<template>
  <Panel>
    <div class="member-condition">
      <div class="member-condition__header">
        <!-- Shaft name -->
        <div class="overview__description typo-super-tiny">
          {{ $t('shafts.dashboard.members_by_replacement_priority') }}
        </div>
        <Select
          :items="elementTypes"
          v-model="elementType"
          :modifiers="['base']"
        />
      </div>
      <div v-if="breakdown && !loading" class="member-condition__container">
        <!-- Shaft condition details -->
        <div class="table">
          <div class="typo-smallish table__title">
            {{ $t('shafts.condition.condition') }}
          </div>
          <div class="typo-smallish table__title text-center">
            {{ $t('shafts.condition.strength') }}
          </div>
          <div
            class="typo-smallish table__title text-right"
            v-for="(data, group) in groups"
            :key="group"
          >
            {{ renderStationOrName(elementType, group) }}
          </div>
          <div class="typo-smallish table__title text-right">
            {{ $t('shafts.condition.total') }}
          </div>
          <template v-for="member in members" :key="member.condition">
            <div class="table__item table__item-condition typo-tiny">
              <div :class="`table_item-rating table__item-rating--${member.condition}`">
              </div>
              {{ member.condition }}
            </div>
            <div class="strength table__item typo-tiny text-center">
              {{ member.strength }}
            </div>
            <div
              v-for="(data, group) in groups"
              :key="group"
              class="typo-tiny table__item table__item--bold text-right"
            >
              {{ data[member.condition] }}
            </div>
            <div class="total table__item typo-tiny text-right table__item--bold">
              {{ totalForCondition(member.condition) }}
            </div>
          </template>
        </div>
      </div>
      <Loader v-if="loading" />
    </div>
  </Panel>
</template>

<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';

import Loader from '@/components/generic/loader.vue';
import Panel from '@/components/dashboard/panel.vue';
import Select from '@/components/form/select.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
});

const ajax = useAjax();
const loading = ref(false);

const breakdown = ref(null);
const elementType = ref(null);
const elementTypes = ref([]);

const members = [5, 4, 3, 2, 1].map((x) => ({
  condition: x,
  strength: I18n.t(`condition_percentages.${x}`),
}));

onMounted(() => {
  loadConditionBreakdown();
});

async function loadConditionBreakdown() {
  loading.value = true;

  let { ok, data } = await ajax(`/organisations/${props.organisationId}/shafts/${props.shaftId}/dashboard/condition_breakdown`);

  if (ok) {
    breakdown.value = data;
    elementTypes.value = Object.keys(breakdown.value).map((t) => ({
      value: t,
      label: I18n.t(`shafts.element_types.shaft.${t}`),
    }));
    elementType.value = elementTypes.value[0].value;
  }

  loading.value = false;
}

const groups = computed(() => {
  if (!breakdown.value || !elementType.value) return {};

  return breakdown.value[elementType.value];
});

watch(() => props.shaftId, loadConditionBreakdown);

function totalForCondition(condition) {
  return Object.values(groups.value).map((group) => group[condition]).reduce((a, b) => a + b, 0);
}

function renderStationOrName(type, item) {
  return type === 'station_members' ? I18n.t(`shafts.element_types.station.${item}`) : item;
}
</script>
